import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { fromJS } from 'immutable';
import * as _ from 'lodash';
import validator from 'validator';
import ReactResizeDetector from 'react-resize-detector';
import moment from 'moment';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import injectReducer from 'utils/injectReducer';
import DatePicker from '../components/DatePicker/index';
import PageSectionHeader from '../components/PageSectionHeader';
import TimePickerInput from '../../../components/TimePickerInput';
import { makeSelectBaggageAndPersonalItemsTravellerTugo, makeSelectBaggageAndPersonalItemsTravellerTugoErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import WizardNavigationButtons from '../components/WizardNavigationButtons';
import { handleFormDataChange, nextSlide, resetFormFields } from './actions';
import { ErrorMessage, Validate, ValidateGroup } from '../../../components/Validation';
import { saveFormData } from '../actions';
import { AdditionalList } from '../Tugo.ADAndDBenefits/components/Additional';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { required, checkDateOnly } from '../../../utils/validation/validator';
import { makeSelectInsuredsList, makeSelectIsClaimForFieldEnabled, makeSelectFormHasChanges } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';

const OPTION_LOST_OR_DAMAGED_BAGGAGE = 'Lost or Damaged Baggage';
const OPTION_STOLEN_BAGGAGE = 'Stolen Baggage';
const OPTION_CASH = 'Cash';
const OPTION_DELAYED_BAGGAGE = 'Delayed Baggage';

export class BaggageAndPersonalItemsTravellerTugo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = _.uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onResize() {
    const { adjust } = this.props;
    if (adjust) adjust();
  }

  onChange = (propertyName) => (event) => {
    const propertyFullName = `form.${propertyName}`;
    if (event === null) {
      this.props.onFormDataChange(propertyFullName, null);
      this.props.setFormHasChanges(true);
      return;
    }

    if (event.constructor && event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyFullName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      this.props.onFormDataChange(propertyFullName, value);
    }
    this.props.setFormHasChanges(true);
  };

  handleDayChange = (propertyName) => (day) => {
    const dayWithoutTimeZone = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, dayWithoutTimeZone);
    this.props.setFormHasChanges(true);
  };

  handleExpenseTypeChange = (formField) => (data) => {
    this.props.onFormDataChange(`form.${formField}`, fromJS(data));
    this.props.setFormHasChanges(true);
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  handleExpensesSelected = (propertyName) => (event) => {
    const { whatExpensesAreYouClaimingFor } = this.props.form;
    const expensesSelected = Array.isArray(whatExpensesAreYouClaimingFor) ? [...whatExpensesAreYouClaimingFor] : [];
    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (value) {
      if (!expensesSelected.includes(name)) expensesSelected.push(name);
    } else {
      const idx = expensesSelected.indexOf(name);
      if (idx !== -1) expensesSelected.splice(idx, 1);
      if (name === OPTION_LOST_OR_DAMAGED_BAGGAGE) {
        this.props.onResetFormFields([
          'wasLostOrDamagedByAirline',
          'numberOfCheckedBags',
          'baggageCheckNumbers',
          'dateBaggageCheckIssued',
          'dateLossReportedToAirline',
          'dateClaimedWithAirline',
          'lostOrDamagedAirlineItems',
          'wasLostOrDamagedSomewhereElse',
          'descriptionBaggageLostOrDamaged',
          'otherLossOrDamageItems',
        ]);
      } else if (name === OPTION_STOLEN_BAGGAGE) {
        this.props.onResetFormFields([
          'descriptionBaggageStolen',
          'authorityReportedTheft',
          'stolenBaggageItems',
        ]);
      } else if (name === OPTION_CASH) {
        this.props.onResetFormFields(['cashItems']);
      } else if (name === OPTION_DELAYED_BAGGAGE) {
        this.props.onResetFormFields([
          'dateBaggageSupposedToArrive',
          'timeBaggageSupposedToArrive',
          'dateBaggageActuallyArrived',
          'timeBaggageActuallyArrived',
          'baggageDelayItems',
        ]);
      }
    }
    this.props.onFormDataChange(propertyName, expensesSelected);
    this.props.setFormHasChanges(true);
  };

  appendIds = (array) =>
    (array || []).map((item) => {
      const newItem = { ...item };
      if (!newItem.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  validateTableField(groups) {
    return _.some(groups, (fields) =>
      _.some(fields, (value) => {
        const newValue = value ? value.toString() : '';
        return validator.isEmpty(newValue);
      })
    );
  }

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState((s) => ({ validationCounter: s.validationCounter + 1 }));
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState((s) => ({ validationCounter: s.validationCounter + 1 }));
    } else {
      this.props.makeValid(this.props.step);
    }

    this.props.onFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') return;
    if (e.key === 'Tab') e.preventDefault();
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.props.onFormDataSave();
    if (this.nextButton) this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  renderExpensesItems(formField) {
    const items = this.props.form[formField];
    const itemsArray = Array.isArray(items) ? items : [];
    const newItems = this.appendIds(itemsArray);
    const { isSummary, submitted, insuredsList, isClaimForFieldEnabled, intl } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="mt-2">
        <AdditionalList
          data={newItems}
          onChange={this.handleExpenseTypeChange(formField)}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
          insuredsList={insuredsList}
          isClaimForFieldEnabled={isClaimForFieldEnabled}
          intl={intl}
        />
      </div>
    );
  }

  render() {
    const { isSummary, submitted, loading, intl } = this.props;
    const form = isSummary ? _.mapValues(this.props.form, (v) => (v == null ? 'N/A' : v)) : this.props.form;
    const whatExpensesAreYouClaimingFor = form.whatExpensesAreYouClaimingFor;
    const isReadOnly = submitted || isSummary;
    const expensesSelected = Array.isArray(whatExpensesAreYouClaimingFor) ? whatExpensesAreYouClaimingFor : [];

    const {
      wasLostOrDamagedByAirline,
      numberOfCheckedBags,
      baggageCheckNumbers,
      dateBaggageCheckIssued,
      dateLossReportedToAirline,
      dateClaimedWithAirline,
      wasLostOrDamagedSomewhereElse,
      descriptionBaggageLostOrDamaged,
      descriptionBaggageStolen,
      authorityReportedTheft,
      dateBaggageSupposedToArrive,
      timeBaggageSupposedToArrive,
      dateBaggageActuallyArrived,
      timeBaggageActuallyArrived,
    } = form;

    const parsedTimeSupposed = timeBaggageSupposedToArrive ? moment(timeBaggageSupposedToArrive) : null;
    const parsedTimeActually = timeBaggageActuallyArrived ? moment(timeBaggageActuallyArrived) : null;

    return (
      <div className="app-content">
        <PageSectionHeader className="pb-2">
          <FormattedMessage {...messages.sectionDescription} />
        </PageSectionHeader>
        <Validate
          name="whatExpensesAreYouClaimingFor"
          validators={[required]}
          group={this.validatorGroup}
          addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
          validChangeInGroup={this.validatorGroup.validChangeInGroup}
          validationCounter={this.state.validationCounter}
          validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
          defaultValue={expensesSelected}
          fieldName={messages.whatExpensesAreYouClaimingFor}
        >
          <div className="row">
            <div className="col-sm-12 form-group pt-3">
              {/* Checkbox 1 — "Lost or Damaged Baggage" */}
              <div className="pt-2">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name={OPTION_LOST_OR_DAMAGED_BAGGAGE}
                  checked={expensesSelected.includes(OPTION_LOST_OR_DAMAGED_BAGGAGE)}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span className="pl-2">
                  <FormattedMessage {...messages.lostOrDamagedBaggage} />
                </span>
              </div>
              {expensesSelected.includes(OPTION_LOST_OR_DAMAGED_BAGGAGE) && (
                <div className="col-sm-12 form-group ml-2 mt-2 p-3 rounded">
                  <div className="row">
                    <Validate
                      name="wasLostOrDamagedByAirline"
                      validators={[required]}
                      group={this.validatorGroup}
                      addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                      validChangeInGroup={this.validatorGroup.validChangeInGroup}
                      validationCounter={this.state.validationCounter}
                      validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                      defaultValue={wasLostOrDamagedByAirline}
                      fieldName={messages.wasBaggageLostOrDamagedWhileInPossessionOfAirline}
                    >
                      <div className="col-sm-12 form-group pl-0 pr-0">
                        <div>
                          <label className="mr-3">
                            <input
                              type="radio"
                              name="wasLostOrDamagedByAirline"
                              value="Yes"
                              checked={wasLostOrDamagedByAirline === 'Yes'}
                              disabled={isReadOnly}
                              onChange={this.onChange('wasLostOrDamagedByAirline')}
                            />
                            {' Yes'}
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="wasLostOrDamagedByAirline"
                              value="No"
                              checked={wasLostOrDamagedByAirline === 'No'}
                              disabled={isReadOnly}
                              onChange={this.onChange('wasLostOrDamagedByAirline')}
                            />
                            {' No'}
                          </label>
                        </div>
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.wasBaggageLostOrDamagedWhileInPossessionOfAirline} />
                        </ErrorMessage>
                      </div>
                    </Validate>
                  </div>

                  {/* If YES, show mandatory fields and loss/damage airport/airline expenses */}
                  {wasLostOrDamagedByAirline === 'Yes' && (
                    <div className="col-sm-12 form-group pl-0 pr-0">
                      <div className="row">
                        <div className="col-sm-6 form-group">
                          <Validate
                            name="numberOfCheckedBags"
                            validators={[required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={numberOfCheckedBags}
                            fieldName={messages.numberOfCheckedBags}
                          >
                            <input
                              type="text"
                              className="form-control"
                              name="numberOfCheckedBags"
                              value={numberOfCheckedBags || ''}
                              disabled={isReadOnly}
                              onChange={this.onChange('numberOfCheckedBags')}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.isRequired} values={messages.numberOfCheckedBags} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                        <div className="col-sm-6 form-group">
                          <Validate
                            name="baggageCheckNumbers"
                            validators={[required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={baggageCheckNumbers}
                            fieldName={messages.baggageCheckNumbers}
                          >
                            <input
                              type="text"
                              className="form-control"
                              name="baggageCheckNumbers"
                              value={baggageCheckNumbers || ''}
                              disabled={isReadOnly}
                              onChange={this.onChange('baggageCheckNumbers')}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.isRequired} values={messages.baggageCheckNumbers} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-6 form-group">
                          <Validate
                            name="dateBaggageCheckIssued"
                            validators={[checkDateOnly, required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={dateBaggageCheckIssued}
                            fieldName={messages.dateBaggageCheckIssued}
                          >
                            <DatePicker
                              position="bottom-start"
                              disabled={isReadOnly}
                              name="dateBaggageCheckIssued"
                              type="text"
                              onDayChange={this.handleDayChange('form.dateBaggageCheckIssued')}
                              value={dateBaggageCheckIssued}
                              placeholder=""
                              allowFutureDates={false}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.isRequired} values={messages.dateBaggageCheckIssued} />
                            </ErrorMessage>
                            <ErrorMessage for="checkDateOnly">
                              <FormattedMessage {...messages.checkEventDate} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                        <div className="col-sm-6 form-group">
                          <Validate
                            name="dateLossReportedToAirline"
                            validators={[checkDateOnly, required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={dateLossReportedToAirline}
                            fieldName={messages.dateLossReportedToAirline}
                          >
                            <DatePicker
                              position="bottom-start"
                              disabled={isReadOnly}
                              name="dateLossReportedToAirline"
                              type="text"
                              onDayChange={this.handleDayChange('form.dateLossReportedToAirline')}
                              value={dateLossReportedToAirline}
                              placeholder=""
                              allowFutureDates={false}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.isRequired} values={messages.dateLossReportedToAirline} />
                            </ErrorMessage>
                            <ErrorMessage for="checkDateOnly">
                              <FormattedMessage {...messages.checkEventDate} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-6 form-group">
                          <Validate
                            name="dateClaimedWithAirline"
                            validators={[checkDateOnly, required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={dateClaimedWithAirline}
                            fieldName={messages.dateClaimedWithAirline}
                          >
                            <DatePicker
                              position="bottom-start"
                              disabled={isReadOnly}
                              name="dateClaimedWithAirline"
                              type="text"
                              onDayChange={this.handleDayChange('form.dateClaimedWithAirline')}
                              value={dateClaimedWithAirline}
                              placeholder=""
                              allowFutureDates={false}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.isRequired} values={messages.dateClaimedWithAirline} />
                            </ErrorMessage>
                            <ErrorMessage for="checkDateOnly">
                              <FormattedMessage {...messages.checkEventDate} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="mt-3">
                        <PageSectionHeader className="pb-2 h5 font-weight-bold">
                          Loss or Damaged by Airline
                        </PageSectionHeader>
                        {this.renderExpensesItems('lostOrDamagedAirlineItems')}
                      </div>
                    </div>
                  )}

                  {/* If NO, show other checks */}
                  {wasLostOrDamagedByAirline === 'No' && (
                    <div className="col-sm-12 form-group pl-0 pr-0">
                      <div className="row">
                        <Validate
                          name="wasLostOrDamagedSomewhereElse"
                          validators={[required]}
                          group={this.validatorGroup}
                          addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                          validChangeInGroup={this.validatorGroup.validChangeInGroup}
                          validationCounter={this.state.validationCounter}
                          validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                          defaultValue={wasLostOrDamagedSomewhereElse}
                          fieldName={messages.wasBaggageLostOrDamagedSomewhereElse}
                        >
                          <div className="col-sm-12 form-group pl-0 pr-0">
                            <div>
                              <label className="mr-3">
                                <input
                                  type="radio"
                                  name="wasLostOrDamagedSomewhereElse"
                                  value="Yes"
                                  checked={wasLostOrDamagedSomewhereElse === 'Yes'}
                                  disabled={isReadOnly}
                                  onChange={this.onChange('wasLostOrDamagedSomewhereElse')}
                                />
                                {' Yes'}
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name="wasLostOrDamagedSomewhereElse"
                                  value="No"
                                  checked={wasLostOrDamagedSomewhereElse === 'No'}
                                  disabled={isReadOnly}
                                  onChange={this.onChange('wasLostOrDamagedSomewhereElse')}
                                />
                                {' No'}
                              </label>
                            </div>
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.isRequired} values={messages.wasBaggageLostOrDamagedSomewhereElse} />
                            </ErrorMessage>
                          </div>
                        </Validate>
                      </div>

                      {wasLostOrDamagedSomewhereElse === 'Yes' && (
                        <div className="col-sm-12 form-group pl-0 pr-0">
                          <div className="row">
                            <div className="col-sm-12 form-group">
                              <Validate
                                name="descriptionBaggageLostOrDamaged"
                                validators={[required]}
                                group={this.validatorGroup}
                                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                                validationCounter={this.state.validationCounter}
                                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                                defaultValue={descriptionBaggageLostOrDamaged}
                                fieldName={messages.describeBaggageLostOrDamaged}
                              >
                                <textarea
                                  className="form-control"
                                  name="descriptionBaggageLostOrDamaged"
                                  value={descriptionBaggageLostOrDamaged || ''}
                                  disabled={isReadOnly}
                                  onChange={this.onChange('descriptionBaggageLostOrDamaged')}
                                  rows="3"
                                />
                                <ErrorMessage for="required">
                                  <FormattedMessage {...messages.isRequired} values={messages.describeBaggageLostOrDamaged} />
                                </ErrorMessage>
                              </Validate>
                            </div>
                          </div>
                          <div className="mt-3">
                            <PageSectionHeader className="pb-2 h5 font-weight-bold">
                              Other Loss or Damage
                            </PageSectionHeader>
                            {this.renderExpensesItems('otherLossOrDamageItems')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Checkbox 2 — "Stolen Baggage" */}
              <div className="pt-2">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name={OPTION_STOLEN_BAGGAGE}
                  checked={expensesSelected.includes(OPTION_STOLEN_BAGGAGE)}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span className="pl-2">
                  <FormattedMessage {...messages.stolenBaggage} />
                </span>
              </div>
              {expensesSelected.includes(OPTION_STOLEN_BAGGAGE) && (
                <div className="col-sm-12 form-group ml-2 mt-2 p-3 rounded">
                  <div className="row">
                    <div className="col-sm-12 form-group">
                      <Validate
                        name="descriptionBaggageStolen"
                        validators={[required]}
                        group={this.validatorGroup}
                        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                        validChangeInGroup={this.validatorGroup.validChangeInGroup}
                        validationCounter={this.state.validationCounter}
                        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                        defaultValue={descriptionBaggageStolen}
                        fieldName={messages.describeBaggageLostOrDamaged}
                      >
                        <textarea
                          className="form-control"
                          name="descriptionBaggageStolen"
                          value={descriptionBaggageStolen || ''}
                          disabled={isReadOnly}
                          onChange={this.onChange('descriptionBaggageStolen')}
                          rows="3"
                        />
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.describeBaggageLostOrDamaged} />
                        </ErrorMessage>
                      </Validate>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12 form-group">
                      <Validate
                        name="authorityReportedTheft"
                        validators={[required]}
                        group={this.validatorGroup}
                        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                        validChangeInGroup={this.validatorGroup.validChangeInGroup}
                        validationCounter={this.state.validationCounter}
                        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                        defaultValue={authorityReportedTheft}
                        fieldName={messages.toWhichAuthorityDidYouReportTheTheft}
                      >
                        <input
                          type="text"
                          className="form-control"
                          name="authorityReportedTheft"
                          value={authorityReportedTheft || ''}
                          disabled={isReadOnly}
                          onChange={this.onChange('authorityReportedTheft')}
                        />
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.toWhichAuthorityDidYouReportTheTheft} />
                        </ErrorMessage>
                      </Validate>
                    </div>
                  </div>
                  <div className="mt-3">
                    <PageSectionHeader className="pb-2 h5 font-weight-bold">
                      Robbery or Theft of Baggage
                    </PageSectionHeader>
                    {this.renderExpensesItems('stolenBaggageItems')}
                  </div>
                </div>
              )}

              {/* Checkbox 3 — "Cash" */}
              <div className="pt-2">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name={OPTION_CASH}
                  checked={expensesSelected.includes(OPTION_CASH)}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span className="pl-2">
                  <FormattedMessage {...messages.cash} />
                </span>
              </div>
              {expensesSelected.includes(OPTION_CASH) && (
                <div className="col-sm-12 form-group ml-2 mt-2 p-3 rounded">
                  {this.renderExpensesItems('cashItems')}
                </div>
              )}

              {/* Checkbox 4 — "Delayed Baggage" */}
              <div className="pt-2">
                <input
                  disabled={isReadOnly}
                  type="checkbox"
                  name={OPTION_DELAYED_BAGGAGE}
                  checked={expensesSelected.includes(OPTION_DELAYED_BAGGAGE)}
                  onChange={this.handleExpensesSelected('form.whatExpensesAreYouClaimingFor')}
                />
                <span className="pl-2">
                  <FormattedMessage {...messages.delayedBaggage} />
                </span>
              </div>
              {expensesSelected.includes(OPTION_DELAYED_BAGGAGE) && (
                <div className="col-sm-12 form-group ml-2 mt-2 p-3 rounded">
                  <div className="row">
                    <div className="col-sm-6 form-group">
                      <Validate
                        name="dateBaggageSupposedToArrive"
                        validators={[checkDateOnly, required]}
                        group={this.validatorGroup}
                        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                        validChangeInGroup={this.validatorGroup.validChangeInGroup}
                        validationCounter={this.state.validationCounter}
                        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                        defaultValue={dateBaggageSupposedToArrive}
                        fieldName={messages.dateBaggageSupposedToArrive}
                      >
                        <DatePicker
                          position="bottom-start"
                          disabled={isReadOnly}
                          name="dateBaggageSupposedToArrive"
                          type="text"
                          onDayChange={this.handleDayChange('form.dateBaggageSupposedToArrive')}
                          value={dateBaggageSupposedToArrive}
                          placeholder=""
                          allowFutureDates={false}
                        />
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.dateBaggageSupposedToArrive} />
                        </ErrorMessage>
                        <ErrorMessage for="checkDateOnly">
                          <FormattedMessage {...messages.checkEventDate} />
                        </ErrorMessage>
                      </Validate>
                    </div>
                    <div className="col-sm-6 form-group">
                      <Validate
                        name="timeBaggageSupposedToArrive"
                        validators={[required]}
                        group={this.validatorGroup}
                        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                        validChangeInGroup={this.validatorGroup.validChangeInGroup}
                        validationCounter={this.state.validationCounter}
                        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                        defaultValue={parsedTimeSupposed}
                        fieldName={messages.timeBaggageSupposedToArrive}
                      >
                        <TimePickerInput
                          allowEmpty
                          showSecond={false}
                          onChange={this.onChange('timeBaggageSupposedToArrive')}
                          value={parsedTimeSupposed}
                          disabled={isReadOnly}
                        />
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.timeBaggageSupposedToArrive} />
                        </ErrorMessage>
                      </Validate>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6 form-group">
                      <Validate
                        name="dateBaggageActuallyArrived"
                        validators={[checkDateOnly, required]}
                        group={this.validatorGroup}
                        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                        validChangeInGroup={this.validatorGroup.validChangeInGroup}
                        validationCounter={this.state.validationCounter}
                        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                        defaultValue={dateBaggageActuallyArrived}
                        fieldName={messages.dateBaggageActuallyArrived}
                      >
                        <DatePicker
                          position="bottom-start"
                          disabled={isReadOnly}
                          name="dateBaggageActuallyArrived"
                          type="text"
                          onDayChange={this.handleDayChange('form.dateBaggageActuallyArrived')}
                          value={dateBaggageActuallyArrived}
                          placeholder=""
                          allowFutureDates={false}
                        />
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.dateBaggageActuallyArrived} />
                        </ErrorMessage>
                        <ErrorMessage for="checkDateOnly">
                          <FormattedMessage {...messages.checkEventDate} />
                        </ErrorMessage>
                      </Validate>
                    </div>
                    <div className="col-sm-6 form-group">
                      <Validate
                        name="timeBaggageActuallyArrived"
                        validators={[required]}
                        group={this.validatorGroup}
                        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                        validChangeInGroup={this.validatorGroup.validChangeInGroup}
                        validationCounter={this.state.validationCounter}
                        validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                        defaultValue={parsedTimeActually}
                        fieldName={messages.timeBaggageActuallyArrived}
                      >
                        <TimePickerInput
                          allowEmpty
                          showSecond={false}
                          onChange={this.onChange('timeBaggageActuallyArrived')}
                          value={parsedTimeActually}
                          disabled={isReadOnly}
                        />
                        <ErrorMessage for="required">
                          <FormattedMessage {...messages.isRequired} values={messages.timeBaggageActuallyArrived} />
                        </ErrorMessage>
                      </Validate>
                    </div>
                  </div>
                  <div className="mt-3">
                    <PageSectionHeader className="pb-2 h5 font-weight-bold">
                      Baggage Delay
                    </PageSectionHeader>
                    {this.renderExpensesItems('baggageDelayItems')}
                  </div>
                </div>
              )}
            </div>
          </div>
          <ErrorMessage for="required">
            <FormattedMessage {...messages.isRequired} values={messages.whatExpensesAreYouClaimingFor} />
          </ErrorMessage>
        </Validate>

        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleSaveButtonClick}
            canMoveNext
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            showSpinnerForElement={this.showSpinnerForElement}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => { this.nextButton = ref; }}
          />
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

BaggageAndPersonalItemsTravellerTugo.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  onResetFormFields: PropTypes.func,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  errors: PropTypes.any,
  loading: PropTypes.bool,
  currentStep: PropTypes.number,
  step: PropTypes.number,
  adjust: PropTypes.func,
  onNext: PropTypes.func,
  makeError: PropTypes.func,
  makeValid: PropTypes.func,
  isReadOnly: PropTypes.bool,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  insuredsList: PropTypes.any,
  isClaimForFieldEnabled: PropTypes.bool,
  intl: intlShape.isRequired,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectBaggageAndPersonalItemsTravellerTugo(),
  errors: makeSelectBaggageAndPersonalItemsTravellerTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  insuredsList: makeSelectInsuredsList(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) => dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetFormFields: (fields) => dispatch(resetFormFields(fields)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withReducer = injectReducer({
  key: 'baggageAndPersonalItemsTravellerTugo',
  reducer,
});

export default compose(
  withReducer,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
)(BaggageAndPersonalItemsTravellerTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.BaggageAndPersonalItemsTraveller/index.js